<template>
  <v-card>
    <v-card-title class="primary white--text headline" style="word-break: break-word">
      {{ $t('splitTicketBook') }}
    </v-card-title>
    <v-card-text class="mt-6">
      <p class="subtitle-1 black--text font-weight-medium" style="white-space: pre-line">{{ body }}</p>
      <v-text-field
        v-model="ticketBookName"
        :label="$t('name')"
        maxlength="64"
        counter="64"
        outlined
        dense
        color="black"
        class="mb-2"
      />
      <AccountAutocomplete
        :unsavedTags="true"
        :accountId="accountId"
        :title="$t('account')"
        @account-chosen="selectedAccount = $event"
      />
      <TractAutocomplete
        :clearable="true"
        :propFilter="activeTractFilter"
        :disabled="selectedContractId != null"
        @tract-chosen="selectedTract = $event"
        @tract-cleared="selectedTract = undefined"
      />
      <v-autocomplete
        v-model="selectedContractId"
        :label="$t('contract')"
        :items="eligibleContracts"
        :item-text="contractText"
        :loading="contractsLoading"
        :disabled="selectedTract != null"
        item-value="contractId"
        color="black"
        clearable
      >
        <template #prepend-inner>
          <v-icon>{{ IconHelpers.getIconForObjectType('contract') }}</v-icon>
        </template>
      </v-autocomplete>
    </v-card-text>
    <v-card-actions>
      <v-container class="pa-2">
        <v-row dense>
          <v-col>
            <v-btn class="ml-n4" text outlined color="black" @click="$emit('cancel')">{{ $t('cancel') }}</v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn class="primary mr-n4" :disabled="!effectiveAccountId || !ticketBookName.trim()" @click="confirm">{{ $t('split') }}</v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { TractStatus } from '@/utils/Enumerations.js'
import { contractKeyLabel } from '@/utils/componentHelpers'
import IconHelpers from '@/utils/IconHelpers'

export default {
  name: 'TicketBookSplitDialog',

  components: {
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    TractAutocomplete: () => import('@/components/autocomplete/TractAutocomplete.vue')
  },

  props: {
    body: {
      type: String,
      required: true
    },
    accountId: {
      type: Number,
      default: undefined
    }
  },

  data () {
    return {
      IconHelpers,
      ticketBookName: '',
      selectedAccount: undefined,
      selectedTract: undefined,
      selectedContractId: undefined,
      contracts: [],
      contractsLoading: false
    }
  },

  computed: {
    effectiveAccountId () {
      return this.selectedAccount?.accountId ?? this.accountId
    },
    activeTractFilter () {
      return { status: [TractStatus.Active.value] }
    },
    eligibleContracts () {
      return this.contracts.filter(c => c.requiresExt2 && c.destinationAccountId === this.effectiveAccountId)
    }
  },

  watch: {
    effectiveAccountId () {
      this.selectedContractId = undefined
    }
  },

  async created () {
    this.contractsLoading = true
    try {
      this.contracts = await this.fetchContractsForAutocomplete({
        includeOpen: true,
        includeByProduct: true,
        includeByproductPurchase: true,
        includeByproductHauling: true,
        includeLogYardSale: true
      })
    } finally {
      this.contractsLoading = false
    }
  },

  methods: {
    ...mapActions('contract', ['fetchContractsForAutocomplete']),

    contractText (contract) {
      return contractKeyLabel({
        type: contract.type,
        tract: contract.tract,
        setting: contract.setting,
        fromAccount: contract.fromAccount,
        destinationAccount: contract.destination,
        account: contract.account
      })
    },

    confirm () {
      this.$emit('confirm', {
        name: this.ticketBookName.trim(),
        targetAccountId: this.effectiveAccountId,
        tractId: this.selectedTract?.tractId,
        contractId: this.selectedContractId
      })
    }
  }
}
</script>
